import { IconButton, Modal } from '@bfe/moly';
import { CopyIcon } from '@bfe/moly-icons';
import { Chain } from '@web3/utils';
import { useState } from 'react';
import { Case, Switch } from 'react-if';
import { useCopyToClipboard } from 'react-use';
import { useToastFn } from '../../../hooks';
import { useChainList } from '../../../store/chainList';
import { getDefaultIndex } from '../../../store/config';
import { useLocale } from '../../../store/i18nMessage';
import { WalletTypesNumber } from '../../../types';
import { addHexPrefix } from '../../../utils/common';
import { iconCaretDown } from '../../commonIcon';
import { useCurrentWallet } from '../../../store/userWallet';

export type Web3CopyCloudWalletModalProps = {
  visible: boolean;
  onClose: () => void;
  zIndex?: number;
};

export function Web3CopyCloudWalletModal({ visible, onClose, zIndex = getDefaultIndex() }: Web3CopyCloudWalletModalProps) {
  const { t } = useLocale();
  const { walletType, getAddress } = useCurrentWallet();
  const { evmChains: cloudEvmChains, getChain, mnemonicEvmChains } = useChainList();
  const [showingWallet, setShowWallet] = useState<WalletAddress>();
  const evmChains = walletType === WalletTypesNumber.CLOUD ? cloudEvmChains : mnemonicEvmChains;

  const walletAddresses: WalletAddress[] = [
    {
      title: 'Ethereum',
      tag: 'EVM',
      address: addHexPrefix(getAddress('ETH') || ''),
      chains: evmChains,
    },
    {
      title: 'BTC',
      address: getAddress('BTC') || '',
      chains: [getChain('BTC')!].filter(Boolean),
    },
    {
      title: 'Solana',
      address: getAddress('SOL') || '',
      chains: [getChain('SOL')!].filter(Boolean),
    },
    {
      title: 'Sui',
      address: getAddress('SUI') || '',
      chains: [getChain('SUI')!].filter(Boolean),
    },
    {
      title: 'Aptos',
      address: getAddress('APT') || '',
      chains: [getChain('APT')!].filter(Boolean),
    },
    {
      title: 'Stacks',
      address: getAddress('STX') || '',
      chains: [getChain('STX')!].filter(Boolean),
    },
    {
      title: 'Ton',
      address: getAddress('TON') || '',
      chains: [getChain('TON')!].filter(Boolean),
    },
    {
      title: 'Tron',
      address: getAddress('TRON') || '',
      chains: [getChain('TRON')!].filter(Boolean),
    },
  ].filter((item) => item.address);

  const [, copy] = useCopyToClipboard();
  const toast = useToastFn();

  const handleCopy = (addr: string) => {
    toast.success(t('address_copied', { chain: addr }));
    copy(addr);
    closeModal();
  };
  const closeModal = () => {
    onClose?.();
    setShowWallet(undefined);
  };

  return (
    <Modal
      modal={false}
      zIndex={zIndex}
      open={visible}
      onCancel={closeModal}
      arrowEnable={Boolean(showingWallet)}
      arrowHandler={() => setShowWallet(undefined)}
      title={t('copy_address')}
      footer={null}
      contextWrapper="overflow-auto !px-0 ltr:mr-6 rtl:ml-6 web3-scrollbar"
    >
      <div className=" pl-6 pr-2 h-full">
        <Switch>
          <Case condition={!showingWallet}>
            {walletAddresses.map((item, index) => (
              <div key={index} className={`w-full p-4 rounded  border-bds-gray-ele-border border-solid border ${index > 0 ? 'mt-6' : ''}`}>
                <div className="flex items-center">
                  <h3 className="text-base font-semibold">{item.title}</h3>
                  {item.tag && <span className="mx-2 rounded bg-bds-gray-ele-line px-1 text-xs text-bds-gray-t2 font-medium">{item.tag}</span>}
                  <div className=" ltr:ml-auto rtl:mr-auto inline-flex items-center">
                    {item.chains.slice(0, 6).map((chain, i) => (
                      <img
                        key={i}
                        className={`size-[26px] ${i > 0 ? 'ltr:ml-[-6px] rtl:mr-[-6px]' : ''} rounded-3xl bg-white border-2 border-solid border-bds-gray-ele-border`}
                        src={chain.iconDayUrl}
                        alt=""
                      />
                    ))}
                    {item.chains.length > 6 && (
                      <IconButton
                        className="ltr:ml-2 rtl:mr-2 svg-fill-current"
                        size="x-small"
                        onClick={() => {
                          setShowWallet(item);
                        }}
                      >
                        {iconCaretDown}
                      </IconButton>
                    )}
                  </div>
                </div>
                <div className="flex justify-between cursor-pointer mt-[18px] items-center" onClick={() => handleCopy(item.address)}>
                  <p className="text-xs break-all text-bds-gray-t1-title">{item.address}</p>
                  <CopyIcon width={16} color="var(--bds-gray-t2)" className="ltr:ml-2 rtl:mr-2 flex-none" />
                </div>
              </div>
            ))}
          </Case>
          <Case condition={true}>
            {showingWallet?.chains?.map((item) => (
              <div key={item.chainId} className="px-3 py-3 flex items-center">
                <img src={item.iconDayUrl} className="size-8" alt="" />
                <div className="ltr:ml-[10px] rtl:mr-[10px]">
                  <h3 className="text-sm font-medium">{item.chainName}</h3>
                  <p className="text-xs text-bds-gray-t2 scale-90 origin-left">{showingWallet?.address}</p>
                </div>
                <IconButton size="x-small" onClick={() => handleCopy(showingWallet.address ?? '')} className="ltr:ml-auto rtl:mr-auto">
                  <CopyIcon width={12} color="var(--bds-gray-t2)" />
                </IconButton>
              </div>
            ))}
          </Case>
        </Switch>
      </div>
    </Modal>
  );
}

interface WalletAddress {
  title: string;
  tag?: string;
  address: string;
  chains: Chain[];
}
